/**
 * ブラウザ側のエラーをサーバーに残す。
 *
 * なぜ必要か:
 *   利用者から不具合の連絡を受けたとき、手元に残っていたのは
 *   Edge Function のログ（サーバー側）だけだった。画面が落ちても、
 *   送信が失敗しても、ブラウザ側の痕跡はどこにも残らない。
 *   しかも本番の logger.error は実際のエラーを捨てて
 *   'An error occurred' とだけ出していたので、console にも何も残らなかった。
 *
 * 方針:
 *   - **報告の本文は送らない。** message と stack だけ。
 *   - 溢れさせない。同じ文言は1回、1セッションの合計にも上限を置く。
 *   - 送信に失敗しても何もしない。エラー報告のエラーを追いかけない。
 */

import { getSupabase } from './supabase';

type Kind = 'uncaught' | 'rejection' | 'boundary' | 'flow';

/** 1セッションで送る上限。ループで壊れたときに何百件も送らせない。 */
const MAX_PER_SESSION = 20;
/** 同じ文言は1回だけ送る。再レンダリングで何度も同じものが来る。 */
const seen = new Set<string>();
let sent = 0;
/** 報告中に発生したエラーで再帰しないための鍵。 */
let reporting = false;

/** どのビルドで起きたかを識別する。Vite が埋める値を使う。 */
const RELEASE =
  (import.meta.env.VITE_COMMIT_SHA as string | undefined)?.slice(0, 12) ??
  (import.meta.env.MODE as string | undefined) ??
  'unknown';

function describe(err: unknown): { message: string; stack?: string } {
  if (err instanceof Error) {
    return { message: `${err.name}: ${err.message}`, stack: err.stack };
  }
  if (typeof err === 'string') return { message: err };
  try {
    return { message: JSON.stringify(err).slice(0, 500) };
  } catch {
    return { message: String(err) };
  }
}

/**
 * エラーを1件送る。失敗しても投げない。
 *
 * @param kind 発生源。uncaught / rejection / boundary / flow
 * @param err  Error でも文字列でもよい
 * @param note 状況の補足（どの操作で起きたか）。本文や個人情報は入れない
 */
export function reportError(kind: Kind, err: unknown, note?: string): void {
  if (reporting || sent >= MAX_PER_SESSION) return;

  const { message: base, stack } = describe(err);
  const message = note ? `[${note}] ${base}` : base;
  /* 同じ文言＋同じ発生源は1回だけ。key に stack を含めない——
     行番号が揺れるだけで同じ不具合が何度も送られる。 */
  const key = `${kind}:${message}`;
  if (seen.has(key)) return;
  seen.add(key);
  sent += 1;

  reporting = true;
  void (async () => {
    try {
      const supabase = getSupabase();
      if (!supabase) return;
      await supabase.functions.invoke('genbasafeai_client-error', {
        body: {
          kind,
          message,
          stack,
          path: typeof location !== 'undefined' ? location.pathname : undefined,
          /* **画面名も送る。** 管理画面は遷移しても URL が変わらないため、
             path だけではダッシュボードで起きたエラーも `/login` と
             記録されてしまい、どこで起きたか分からない
             （実際に顧客のエラーを追う際に詰まった）。 */
          view: (window as unknown as { __anzenView?: string }).__anzenView,
          release: RELEASE,
        },
      });
    } catch {
      /* 送れなくても何もしない。ここで再度報告すると無限に回る。 */
    } finally {
      reporting = false;
    }
  })();
}

/**
 * 捕まえていないエラーを拾う。アプリの起動時に1回だけ呼ぶ。
 *
 * これが無いと、レンダリング外の例外（イベントハンドラ、非同期処理）が
 * どこにも残らない。ErrorBoundary は render 中の例外しか捕まえない。
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { __anzenErrorHandlersInstalled?: boolean };
  if (w.__anzenErrorHandlersInstalled) return;
  w.__anzenErrorHandlersInstalled = true;

  window.addEventListener('error', (e) => {
    /* 画像や script の読み込み失敗も error イベントで来る。
       それは ErrorEvent の error を持たないので文言で残す。 */
    /* **発生元のファイルも残す。**
       別オリジンのスクリプトで起きた例外はブラウザが
       `Script error.` としか教えてくれない（本文もスタックも消される）。
       filename だけは取れることがあるので、どのスクリプトかを残す。
       実際に「Script error.」が1件記録され、どこで起きたか分からなかった。
       このアプリは Tailwind を CDN から実行時に読んでいるため、
       別オリジンの例外は起こり得る。 */
    const where = e.filename
      ? `${e.filename}:${e.lineno ?? '?'}:${e.colno ?? '?'}`
      : undefined;
    reportError('uncaught', e.error ?? e.message ?? '不明なエラー', where);
  });

  window.addEventListener('unhandledrejection', (e) => {
    reportError('rejection', e.reason ?? '不明な理由');
  });
}
